import React, { useEffect, useState } from 'react';
import { defaultInvoicePdfScenario, invoiceAllowsPdfScenario } from '../utils/invoiceLedger';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  Input,
  Typography,
  Stack,
  Divider,
  RadioGroup,
  Radio
} from '@mui/joy';

export type InvoicePdfScenario =
  | 'total_with_deposit_breakdown'
  | 'total_without_deposit_breakdown'
  | 'deposit_only'
  | 'outstanding_only'
  | 'no_vat_no_deposit'
  | 'stage_only';

interface DocumentDatePickerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (date: string, invoiceScenario?: InvoicePdfScenario | null, invoiceStageIndex?: number | null) => void;
  defaultDate: string; // ISO string
  dateLocked?: boolean;
  documentTitle: string;
  showInvoiceScenario?: boolean;
  defaultInvoiceScenario?: InvoicePdfScenario;
  invoiceRole?: string | null;
  invoiceStageOptions?: Array<{ index: number; label: string; pct: number }>;
  defaultInvoiceStageIndex?: number | null;
}

export const DocumentDatePickerModal: React.FC<DocumentDatePickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  defaultDate,
  dateLocked,
  documentTitle,
  showInvoiceScenario,
  defaultInvoiceScenario,
  invoiceRole,
  invoiceStageOptions,
  defaultInvoiceStageIndex
}) => {
  const hasStageOptions = Array.isArray(invoiceStageOptions) && invoiceStageOptions.length > 0;
  const showScenarioChoices = !!showInvoiceScenario && (
    invoiceAllowsPdfScenario(invoiceRole, 'total_without_deposit_breakdown')
    || invoiceAllowsPdfScenario(invoiceRole, 'deposit_only')
    || invoiceAllowsPdfScenario(invoiceRole, 'outstanding_only')
    || invoiceAllowsPdfScenario(invoiceRole, 'no_vat_no_deposit')
    || hasStageOptions
  );
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [selectedInvoiceScenario, setSelectedInvoiceScenario] = useState<InvoicePdfScenario>(
    defaultInvoiceScenario || defaultInvoicePdfScenario(invoiceRole)
  );
  const [selectedInvoiceStageIndex, setSelectedInvoiceStageIndex] = useState<number | null>(defaultInvoiceStageIndex ?? null);
  const [invoiceHelpOpen, setInvoiceHelpOpen] = useState(false);

  useEffect(() => {
    if (!hasStageOptions && selectedInvoiceScenario === 'stage_only') {
      setSelectedInvoiceScenario('outstanding_only');
      return;
    }
    if (hasStageOptions && (selectedInvoiceScenario === 'deposit_only' || selectedInvoiceScenario === 'outstanding_only')) {
      setSelectedInvoiceScenario('stage_only');
      if (selectedInvoiceStageIndex == null) {
        setSelectedInvoiceStageIndex(0);
      }
      return;
    }
    if (showInvoiceScenario && !invoiceAllowsPdfScenario(invoiceRole, selectedInvoiceScenario)) {
      setSelectedInvoiceScenario(defaultInvoicePdfScenario(invoiceRole));
    }
  }, [hasStageOptions, selectedInvoiceScenario, selectedInvoiceStageIndex, showInvoiceScenario, invoiceRole]);

  const handleConfirm = () => {
    if (showInvoiceScenario && selectedInvoiceScenario === 'stage_only') {
      if (!Array.isArray(invoiceStageOptions) || invoiceStageOptions.length === 0) {
        window.alert('No payment stages are available for this invoice.');
        return;
      }
      if (selectedInvoiceStageIndex == null) {
        window.alert('Please select a stage to export.');
        return;
      }
    }
    const scenario = showInvoiceScenario
      ? (invoiceAllowsPdfScenario(invoiceRole, selectedInvoiceScenario)
        ? selectedInvoiceScenario
        : defaultInvoicePdfScenario(invoiceRole))
      : null;
    onConfirm(
      selectedDate,
      scenario,
      scenario === 'stage_only' ? selectedInvoiceStageIndex : null
    );
    onClose();
  };

  const handleCancel = () => {
    setSelectedDate(defaultDate); // Reset to original
    setSelectedInvoiceScenario(defaultInvoiceScenario || defaultInvoicePdfScenario(invoiceRole));
    setSelectedInvoiceStageIndex(defaultInvoiceStageIndex ?? null);
    setInvoiceHelpOpen(false);
    onClose();
  };

  const formatDateForDisplay = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = (n: number) => {
      const j = n % 10, k = n % 100;
      if (j === 1 && k !== 11) return 'st';
      if (j === 2 && k !== 12) return 'nd';
      if (j === 3 && k !== 13) return 'rd';
      return 'th';
    };
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    return `${day}${suffix(day)} ${month} ${year}`;
  };

  return (
    <>
    <Modal open={open} onClose={handleCancel}>
      <ModalDialog size="sm">
        <ModalClose />
        <DialogTitle>Set Document Date</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography level="body-sm" color="neutral">
              {showInvoiceScenario
                ? (dateLocked
                  ? 'This invoice date is locked after FreeAgent issue.'
                  : 'This invoice date is the date of issue.')
                : 'This date will be remembered for future exports.'}
            </Typography>
            
            <FormControl>
              <FormLabel>{showInvoiceScenario ? 'Invoice date' : 'Document Date'}</FormLabel>
              <Input
                type="date"
                value={selectedDate.split('T')[0]}
                disabled={dateLocked}
                onChange={(e) => {
                  const newDate = new Date(e.target.value);
                  newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
                  setSelectedDate(newDate.toISOString());
                }}
              />
            </FormControl>

            <Typography level="body-sm" color="primary">
              Preview: {formatDateForDisplay(selectedDate)}
            </Typography>

            {showScenarioChoices && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel>Invoice PDF type</FormLabel>
                  <RadioGroup
                    value={selectedInvoiceScenario}
                    onChange={(e) => setSelectedInvoiceScenario(e.target.value as InvoicePdfScenario)}
                  >
                    <Radio value="total_with_deposit_breakdown" label="Full Invoice Details (including deposit)" />
                    {invoiceAllowsPdfScenario(invoiceRole, 'total_without_deposit_breakdown') && (
                      <Radio value="total_without_deposit_breakdown" label="Full Invoice Details (excluding deposit)" />
                    )}
                    {!hasStageOptions && invoiceAllowsPdfScenario(invoiceRole, 'deposit_only') && (
                      <Radio value="deposit_only" label="Deposit only" />
                    )}
                    {!hasStageOptions && invoiceAllowsPdfScenario(invoiceRole, 'outstanding_only') && (
                      <Radio value="outstanding_only" label="Outstanding Amount" />
                    )}
                    {hasStageOptions && <Radio value="stage_only" label="Specific payment stage" />}
                    {invoiceAllowsPdfScenario(invoiceRole, 'no_vat_no_deposit') && (
                      <Radio value="no_vat_no_deposit" label="FULL AMOUNT no VAT and no Deposit (edge case)" />
                    )}
                  </RadioGroup>
                </FormControl>
                {selectedInvoiceScenario === 'stage_only' && (
                  <FormControl>
                    <FormLabel>Select stage</FormLabel>
                    <RadioGroup
                      value={selectedInvoiceStageIndex != null ? String(selectedInvoiceStageIndex) : ''}
                      onChange={(e) => setSelectedInvoiceStageIndex(Number(e.target.value))}
                    >
                      {(invoiceStageOptions || []).map((stage) => (
                        <Radio
                          key={stage.index}
                          value={String(stage.index)}
                          label={stage.index === 0
                            ? `${stage.label} (Deposit) (${stage.pct}%)`
                            : `${stage.label} (${stage.pct}%)`}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            )}

          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            {showScenarioChoices && (
              <Button variant="soft" onClick={() => setInvoiceHelpOpen(true)}>
                Help?
              </Button>
            )}
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>
              Use This Date
            </Button>
          </Stack>
        </DialogActions>
      </ModalDialog>

    </Modal>

    <Modal open={invoiceHelpOpen} onClose={() => setInvoiceHelpOpen(false)}>
      <ModalDialog size="md">
        <ModalClose />
        <DialogTitle>Invoice PDF Type Help</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25}>
            <Typography level="body-sm">
              Each option changes which totals are emphasised in the exported invoice PDF.
            </Typography>
            <Typography level="body-sm">
              <strong>Full Invoice Details (including deposit)</strong>: Shows this invoice’s own totals plus the deposit already invoiced as a breakdown row. A deposit invoice skips that row — the line item is the deposit.
            </Typography>
            {invoiceAllowsPdfScenario(invoiceRole, 'total_without_deposit_breakdown') && (
              <Typography level="body-sm">
                <strong>Full Invoice Details (excluding deposit)</strong>: Shows this invoice’s own totals. The deposit already invoiced is noted below the table, not as a line item. This is the default for export and Send.
              </Typography>
            )}
            {invoiceAllowsPdfScenario(invoiceRole, 'deposit_only') && (
              <Typography level="body-sm">
                <strong>Deposit only</strong>: Focuses on deposit figures. You can still reference full project total using template variables.
              </Typography>
            )}
            {!hasStageOptions && (
              <Typography level="body-sm">
                <strong>Outstanding Amount</strong>: For a legacy combined invoice only. Subtracts the deposit from the invoice total. Balance, variation, and credit invoices already store the amount due, so they export that figure instead.
              </Typography>
            )}
            {hasStageOptions && (
              <Typography level="body-sm">
                <strong>Specific payment stage</strong>: Exports a single stage amount from the selected payment structure. Stage 1 is the initial payment stage.
              </Typography>
            )}
            {invoiceAllowsPdfScenario(invoiceRole, 'no_vat_no_deposit') && (
              <Typography level="body-sm">
                <strong>FULL AMOUNT no VAT and no Deposit (edge case)</strong>: Suppresses VAT and deposit logic entirely.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setInvoiceHelpOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
    </>
  );
};
















